import {Button, Group, Paper, Progress, Stack, Text} from '@mantine/core';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {RegistrationMailService, type RegistrationMailSendStatus} from '../../client';
import {notifications} from '@mantine/notifications';
import {formatRequestError} from '../../utils/formatRequestError';

type RegistrationMailSendProgressProps = {
    status: RegistrationMailSendStatus | undefined;
    canCancel?: boolean;
};

export function RegistrationMailSendProgress({
    status,
    canCancel = false,
}: RegistrationMailSendProgressProps) {
    const queryClient = useQueryClient();

    const cancelMutation = useMutation({
        mutationFn: RegistrationMailService.cancelRegistrationMailSendApiV1RegistrationMailSendCancelPost,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['registration-mail-send-status']});
            notifications.show({
                color: 'blue',
                title: 'Cancellation requested',
                message: 'The registration mail send will stop after the current mail.',
            });
        },
        onError: (error: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Could not cancel send',
                message: formatRequestError(error, 'Request failed'),
            });
        },
    });

    if (!status?.sending) {
        return null;
    }

    const remaining = Math.max((status.total ?? 0) - (status.sent ?? 0), 0);
    const progress = (status.total ?? 0) > 0
        ? Math.round(((status.sent ?? 0) / (status.total ?? 0)) * 100)
        : 0;

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="xs">
                <Text size="sm" fw={600}>Registration mail send in progress</Text>
                <Progress value={progress} size="lg" radius="xl" />
                <Text size="sm" c="dimmed">
                    {status.sent ?? 0} of {status.total ?? 0} sent ({remaining} remaining)
                </Text>
                {canCancel ? (
                    <Group gap="xs">
                        <Button
                            color="red"
                            variant="outline"
                            loading={cancelMutation.isPending}
                            disabled={cancelMutation.isSuccess}
                            onClick={() => cancelMutation.mutate()}
                        >
                            Cancel send
                        </Button>
                    </Group>
                ) : null}
            </Stack>
        </Paper>
    );
}
